/* eslint-disable no-redeclare, @typescript-eslint/no-redeclare */
import {
  Paper,
  Group,
  Divider,
  Text,
  Autocomplete,
  Select,
  Space,
  Checkbox,
  NumberInput,
  Stack,
  Switch,
} from '@mantine/core';
import React, { FC, useContext } from 'react';
import { BaseDocument, DiscountType, PositionDto } from '../../../Service/DocumentT';
import BookitupSegmentedControl from '../../../Atoms/BookitupSegmentedControl';
import { DataS } from '../../../Service/DataS';
import { FormatS } from '../../../Service/FormatS';
import { TestS } from '../../../TestS';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { EventRichInput, RichInput } from '../../../Organismns/tiptap/TiptapEditorInput';
import { useEventScope } from '../../../Service/hooks/useEventScope';
import { DocumentService } from '../../../Service/helper/DocumentService';
import { TiptapS } from '../../../Service/TiptapS';
import ConfigOption from '../../../Atoms/ConfigOption';
import { LicenceS } from '../../../Service/LicenceS';
import DocumentBubbleMenu from '../../../Organismns/tiptap/toolbars/BubbleMenu';
import FloatNumberInput from '../../../Atoms/inputs/FloatNumberInput';
import PositionAmountInput from './PositionAmountInput';

interface Props {
  position: PositionDto;
  savePatch: (patch: Partial<PositionDto>) => Promise<boolean>;
  bookitupDocument?: BaseDocument | null;
  resourcePresent?: boolean;
  refresh?: (patch: Partial<PositionDto>) => void;
}

const PositionModal: FC<Props> = ({ position, savePatch, bookitupDocument, refresh, resourcePresent = false }) => {
  const { profile } = useContext(ProfileContext);
  const { licence } = useJwtClaims();
  const { isEventScope } = useEventScope();

  const isSmallScaleBusiness = profile && profile.smallScaleBusiness;
  const { taxType, documentType } = bookitupDocument ?? { taxType: 'gross' };
  const {
    discount,
    label,
    subtext,
    price,
    netPrice,
    tax,
    priceUnit,
    netPriceUnit,
    unit,
    optional,
    negotiable,
    min,
    max,
    type: positionKind,
    autoSelect,
  } = position;

  const {
    value: discountValue,
    type: discountType,
    amount: discountAmount,
  } = discount ?? { amount: 0, type: 'PERCENT' };

  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';
  const isOffer = documentType === 'OFFER';
  const finalPrice = taxType === 'gross' ? price : netPrice;
  const hasFlowLicence = LicenceS.hasSuitableLicence('FLOW', licence);
  const isFreeTextPosition = positionKind === 'FREE_TEXT';
  const showPortalSettings = isOffer && !isFreeTextPosition;
  const showAutoSelectSetting = optional;
  const showFreeTextSetting = !isPartialInvoice && hasFlowLicence;
  const showPortalFlowSettings = showPortalSettings && hasFlowLicence;
  const showPositionSettings = showPortalSettings || showFreeTextSetting;

  const handleIntegerKeyDown = (keyEvent: React.KeyboardEvent<HTMLInputElement>) => {
    const navigationKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (
      keyEvent.metaKey ||
      keyEvent.ctrlKey ||
      keyEvent.altKey ||
      navigationKeys.includes(keyEvent.key) ||
      /^\d$/.test(keyEvent.key)
    ) {
      return;
    }

    keyEvent.preventDefault();
  };

  const handleIntegerPaste = (pasteEvent: React.ClipboardEvent<HTMLInputElement>) => {
    if (!/^\d+$/.test(pasteEvent.clipboardData.getData('text'))) {
      pasteEvent.preventDefault();
    }
  };

  return (
    <>
      {isEventScope && (
        <EventRichInput
          label="Beschreibung"
          value={label}
          menu={<DocumentBubbleMenu />}
          patchField={(val) => savePatch({ label: val })}
          styles={TiptapS.TextInputStyles}
        />
      )}
      {!isEventScope && (
        <RichInput
          label="Beschreibung"
          value={label}
          menu={<DocumentBubbleMenu />}
          patchField={(val) => savePatch({ label: val })}
          styles={TiptapS.TextInputStyles}
        />
      )}
      <Space h="xs" />
      {!isEventScope && (
        <RichInput
          label="Unterbeschreibung"
          value={subtext}
          menu={<DocumentBubbleMenu />}
          patchField={(val) => savePatch({ subtext: val })}
          styles={TiptapS.TextInputStyles}
        />
      )}
      {isEventScope && (
        <EventRichInput
          label="Unterbeschreibung"
          value={subtext}
          menu={<DocumentBubbleMenu />}
          patchField={(val) => savePatch({ subtext: val })}
          styles={TiptapS.TextInputStyles}
        />
      )}
      {!isFreeTextPosition && (
        <Group mt="xs">
          <div style={{ marginBottom: 5 }}>
            <PositionAmountInput fullWidth withLabel position={position} onChange={(pos) => refresh?.(pos)} />
          </div>
          <Autocomplete
            label={
              <Text size="xs" color="dimmed">
                Einheit
              </Text>
            }
            defaultValue={unit ?? ''}
            data={DataS.positionUnitSuggestions}
            disabled={isPartialInvoice}
            onBlur={(e) => savePatch({ unit: e.currentTarget.value })}
            sx={{ width: 60 }}
          />
          <FloatNumberInput
            hideControls
            label={
              <Text size="xs" color="dimmed">
                Preis
              </Text>
            }
            decimalSeparator=","
            precision={2}
            removeTrailingZeros
            defaultValue={taxType === 'gross' ? priceUnit : netPriceUnit}
            sx={{ width: 70 }}
            onBlur={(e) => {
              const tmp = FormatS.formatAsCurrency(e.currentTarget.value);
              savePatch({ priceUnit: taxType === 'gross' ? tmp : DocumentService.calculateGrossPrice(tmp, tax, true) });
            }}
            data-testid={TestS.Documents.Edit.Position.DISCOUNT_VALUE}
          />
          {!isSmallScaleBusiness && (
            <Select
              label={
                <Text size="xs" color="dimmed">
                  MwSt
                </Text>
              }
              value={tax.toString()}
              data={DataS.taxRateOptions}
              sx={{ width: 55 }}
              onChange={(val) => savePatch({ tax: Number(val) })}
              // eslint-disable-next-line react/jsx-no-useless-fragment
              rightSection={<></>}
              rightSectionWidth={0}
            />
          )}
        </Group>
      )}
      {/* {resourcePresent && positionType !== 'FREE_TEXT' && (
        <Group mt="xl">
          <Checkbox
            size="xs"
            label={
              <Text color="dimmed" size="xs">
                Ressourceninhalt anzeigen
              </Text>
            }
            checked={positionType === 'RESOURCE'}
            onChange={() => savePatch({ positionType: positionType === 'RESOURCE' ? 'REGULAR' : 'RESOURCE' })}
          />
        </Group>
      )} */}
      {showPositionSettings && (
        <>
          <Divider mt="md" label="Einstellungen" labelPosition="center" />
          <Stack px="xs" mt="xs" spacing="xs">
            {showFreeTextSetting && (
              <ConfigOption
                title="Freitext"
                description="Zeigt nur die Beschreibung und Unterbeschreibung dieser Position an und blendet Preise, Mengen, Ressourcen und Portaloptionen aus."
                rightSection={
                  <Switch
                    size="sm"
                    checked={isFreeTextPosition}
                    onChange={() => savePatch({ type: isFreeTextPosition ? 'REGULAR' : 'FREE_TEXT' })}
                  />
                }
              />
            )}
            {showPortalSettings && (
              <ConfigOption
                title="Optionale Position"
                description="Kunden können diese Position im Portal auswählen oder abwählen."
                rightSection={
                  <Switch size="sm" checked={optional} onChange={() => savePatch({ optional: !optional })} />
                }
              />
            )}
            {showPortalSettings && showAutoSelectSetting && (
              <ConfigOption
                title="Option vorausgewählt"
                description="Diese optionale Position ist im Portal standardmäßig ausgewählt."
                rightSection={
                  <Switch size="sm" checked={autoSelect} onChange={() => savePatch({ autoSelect: !autoSelect })} />
                }
              />
            )}
            {showPortalFlowSettings && (
              <>
                <ConfigOption
                  title="Kunde kann Menge wählen"
                  description="Kunden können die Menge im Portal selbst festlegen. Mindest- und Maximalmenge begrenzen den erlaubten Bereich."
                  rightSection={
                    <Switch size="sm" checked={negotiable} onChange={() => savePatch({ negotiable: !negotiable })} />
                  }
                />
                {negotiable && (
                  <Group spacing={4} align="flex-end" position="right">
                    <NumberInput
                      hideControls
                      label={
                        <Text color="dimmed" size="xs">
                          Mindestmenge
                        </Text>
                      }
                      min={0}
                      precision={0}
                      placeholder="z.B. 1"
                      defaultValue={min}
                      sx={{ width: 100 }}
                      onKeyDown={handleIntegerKeyDown}
                      onPaste={handleIntegerPaste}
                      onBlur={(e) => savePatch({ min: Number(e.currentTarget.value) || 0 })}
                    />
                    <NumberInput
                      hideControls
                      label={
                        <Text color="dimmed" size="xs">
                          Maximalmenge
                        </Text>
                      }
                      min={0}
                      precision={0}
                      placeholder="Kein Limit"
                      defaultValue={max === 0 ? undefined : max}
                      sx={{ width: 100 }}
                      onKeyDown={handleIntegerKeyDown}
                      onPaste={handleIntegerPaste}
                      onBlur={(e) => savePatch({ max: Number(e.currentTarget.value) || 0 })}
                    />
                  </Group>
                )}
              </>
            )}
          </Stack>
        </>
      )}
      {!isFreeTextPosition && (
        <Paper mt="md" shadow="md" p="md">
          {!isPartialInvoice && (
            <>
              <Group position="apart">
                <Text color="dimmed" size="xs">
                  Summe
                </Text>
                <Text>
                  {FormatS.formatCurrencyWithUnitSign(
                    (finalPrice === 0 ? finalPrice : finalPrice + discountAmount).toString(),
                  )}
                </Text>
              </Group>
              <Group position="apart" mt="xs">
                <Group spacing="xs">
                  <Text color="dimmed" size="xs">
                    Rabatt:
                  </Text>
                  <FloatNumberInput
                    hideControls
                    decimalSeparator=","
                    precision={Number.isInteger(discountValue) ? 0 : 2}
                    defaultValue={discountValue}
                    sx={{ width: 60 }}
                    onBlur={(e) => {
                      const intVal = parseFloat(e.target.value.replace(',', '.'));
                      const finalVal = isNaN(intVal) || intVal < 0 ? 0 : intVal;
                      savePatch({ discountValue: finalVal });
                    }}
                  />
                  <BookitupSegmentedControl
                    value={discountType}
                    data={DataS.discountUnitOptions}
                    onChange={(val: DiscountType) => val && savePatch({ discountType: val })}
                    sx={{ marginLeft: -10 }}
                  />
                </Group>
                <Text>{`- ${FormatS.formatCurrencyWithUnitSign(discountAmount.toString())}`}</Text>
              </Group>
              <Divider mb="xs" mt="xs" />
            </>
          )}
          <Group position="apart">
            <Text>Preis:</Text>
            <Text weight="bolder">{FormatS.formatCurrencyWithUnitSign(finalPrice ? finalPrice.toString() : '0')}</Text>
          </Group>
        </Paper>
      )}
    </>
  );
};

export default PositionModal;
